import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments';
import { Native } from '../../native';
import { AUDIT_TOKEN_MESSAGE, fileNameFromHeaders, isTokenRequest } from '../utils/api-file';
import { AuthService } from './auth.service';

const OBJECT_URL_LIFETIME_MS = 60_000
const TOKEN_HANDOFF_TIMEOUT_MS = 60_000

/**
 * API files go through HttpClient so the interceptor adds the identity bearer
 * token. A plain link or window.open would reach the API without it.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiFileService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) { }

  private popupBlocked(): void {
    this.toastr.warning(this.translate.instant('popup_blocked'))
  }

  /** Saves the API response as a file. */
  async download(url: string, fallbackName: string, body?: FormData): Promise<void> {
    const response = await this.http.request(body ? 'POST' : 'GET', url, {
      body,
      observe: 'response',
      responseType: 'blob',
    }).toPromise()
    const name = fileNameFromHeaders((header) => response.headers.get(header), fallbackName)
    if (Native.IsNative()) {
      // The desktop webview ignores <a download>; save through the native dialog.
      await Native.saveBytesToUserPath(new Uint8Array(await response.body.arrayBuffer()), name)
      return
    }
    const objectUrl = URL.createObjectURL(response.body)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = name
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_LIFETIME_MS)
  }

  /**
   * Shows the API response in a new tab. Call it straight from a click: the
   * tab opens before the request so popup blockers allow it.
   */
  async openInNewTab(url: string): Promise<void> {
    const tab = window.open('', '_blank')
    if (!tab) {
      this.popupBlocked()
      return
    }
    tab.opener = null
    try {
      const blob = await this.http.get(url, { responseType: 'blob' }).toPromise()
      const objectUrl = URL.createObjectURL(blob)
      tab.location.href = objectUrl
      setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_LIFETIME_MS)
    } catch (err) {
      tab.close()
      throw err
    }
  }

  /**
   * Opens an API page that runs its own requests, then posts the identity
   * JWT to that page once it asks. The token goes only to the API origin and
   * never into the URL.
   */
  openWithToken(url: string): void {
    // API_URL is relative ("/api/") in local dev.
    const apiOrigin = new URL(environment.API_URL, location.href).origin
    const page = window.open(url, '_blank')
    if (!page) {
      this.popupBlocked()
      return
    }
    const stop = () => {
      window.removeEventListener('message', onMessage)
      clearTimeout(timer)
    }
    const onMessage = async (event: MessageEvent) => {
      if (!isTokenRequest(event, page, apiOrigin)) {
        return
      }
      stop()
      const token = await this.authService.accessToken()
      if (token) {
        page.postMessage({ type: AUDIT_TOKEN_MESSAGE, token }, apiOrigin)
      }
    }
    const timer = setTimeout(stop, TOKEN_HANDOFF_TIMEOUT_MS)
    window.addEventListener('message', onMessage)
  }
}
